import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import { AppContext } from '@prisjakt/ui-app-context';

import { Group } from '@vx/group';
import { Line, Bar } from '@vx/shape';
import { GridRows, GridColumns } from '@vx/grid';
import { AxisLeft, AxisBottom } from '@vx/axis';
import { scaleTime, scaleLinear } from '@vx/scale';

import { withTooltip, Tooltip } from '@vx/tooltip';
import { localPoint } from '@vx/event';
import { extent, max, bisector } from 'd3-array';

import { ParentSize } from '@vx/responsive';

import StorePriceCurve, * as StorePriceCurveHelpers from './components/store-price-curve';

import Legend from './legend';

import * as S from './styles';

const bisectDate = bisector(d => new Date(d.date)).left;

class GraphComponent extends React.PureComponent {
  constructor(props) {
    super(props);

    this.margin = this.props.margin || {
      top: 10,
      bottom: this.props.showXAxis ? 50 : 10,
      left: this.props.showYAxis ? this.guessLeftMargin() : 0,
      right: 0,
    };

    this.handleTooltip = this.handleTooltip.bind(this);
  }

  handleTooltip({ event, data, xFunc, xScale, yScale }) {
    const { showTooltip, width, graphs } = this.props;
    let { x } = localPoint(event);
    x -= this.margin.left;
    const x0 = xScale.invert(x);
    const index = bisectDate(data, x0, 1);
    const d0 = data[index - 1];
    const d1 = data[index];
    let d = d0;

    const isSmallDevice = width < 580;

    // Fix for the tooltip appearing outside the graph
    // covering the Y-axis
    if (x < 0) {
      x = 0;
    }

    if (d1 && d1.date) {
      d = x0 - xFunc(d0.date) > xFunc(d1.date) - x0 ? d1 : d0;
    }

    const tooltipWidth =
      (this.tooltip &&
        // Cannot get a ref for the internal DOM nodes of @vx/tooltip
        // so we need to use `findDOMNode` here
        // eslint-disable-next-line react/no-find-dom-node
        ReactDOM.findDOMNode(this.tooltip).getBoundingClientRect().width) ||
      85;

    // Where the right side of the tooltip hits the edge of the parent container
    const tooltipBound =
      tooltipWidth && width - x - tooltipWidth - this.margin.left;

    // If there are any extra graphs, we need to mix their tooltip data into
    // the basic tooltip data, too.
    const tooltipData = graphs.reduce(
      (obj, g) => (g.tooltipData ? Object.assign(obj, g.tooltipData(d)) : obj),
      {
        xValue: d.date,
        yValue: d.value,
        tooltipBound,
        isSmallDevice,
      }
    );

    showTooltip({
      tooltipData,
      tooltipLeft: x,
      tooltipTop: yScale(d.value),
    });
  }

  static numTicksForWidth(width) {
    if (width <= 400) return 4;
    if (width > 400 && width <= 600) return 6;
    return 10;
  }

  static numTicksForHeight(height) {
    if (height <= 200) return 4;
    if (height > 200 && height <= 400) return 6;
    return 10;
  }

  guessLeftMargin() {
    const maxPrice = Math.max(...this.props.nodes[0].map(({ value }) => value));
    const numChars = this.props.yTickFormat(maxPrice).length;

    if (numChars > 10) {
      return 85;
    }

    if (numChars === 10) {
      return 80;
    }

    if (numChars > 7) {
      return 70;
    }

    return 56;
  }

  render() {
    const {
      width,
      height,
      aspectRatio,
      showXGrid,
      showYGrid,
      showXAxis,
      showYAxis,
      yTickFormat,
      xTickFormat,
      showLegend,
      showGraphTooltip,
      showTooltip,
      hideTooltip,
      tooltipData,
      tooltipTop,
      tooltipLeft,
      graphs,
      storePrices,
      storePriceColors,
      legend,
      xScaleData,
      yScaleData,
    } = this.props;

    const graphHeight = height || width / aspectRatio;
    const margin = this.margin;

    const series = this.props.nodes.filter(node => node.length > 0);
    const data = series.reduce((rec, d) => rec.concat(d), []);

    const xMax = width - margin.left;
    const yMax = graphHeight - margin.top - margin.bottom;

    const xFunc = d => new Date(d.date);

    const storePriceCurves = StorePriceCurveHelpers.getCurves(
      storePrices,
      storePriceColors
    );
    const maxStorePriceY = StorePriceCurveHelpers.getMaxYValue(
      storePriceCurves
    );

    const xScale = scaleTime({
      range: [0, xMax],
      domain: extent(xScaleData),
    });
    const yScale = scaleLinear({
      range: [yMax, 0],
      domain: [0, max(yScaleData.concat(maxStorePriceY)) * 1.1],
      nice: true,
    });

    const graphLegends = graphs
      .filter(g => g.legend)
      .map(g => g.legend)
      .reverse();

    const startDate = new Date(data[0].date);
    const endDate = new Date(data[data.length - 1].date);

    return (
      <>
        {showLegend && (
          <Legend
            lines={graphLegends.map(l => l.lineType)}
            lineLabels={graphLegends.map(l => l.label)}
            colors={graphLegends.map(l => l.color)}
            descriptions={graphLegends.map(l => l.description)}
            extra={legend}
          />
        )}

        <svg width={width} height={graphHeight}>
          <Group top={margin.top} left={margin.left}>
            {showXAxis && (
              <AxisBottom
                scale={xScale}
                top={yMax}
                stroke="#1b1a1e"
                tickFormat={value => xTickFormat(value)}
                numTicks={GraphComponent.numTicksForWidth(width)}
              >
                {axis => (
                  <g>
                    <Line
                      from={{ x: 0, y: 0 }}
                      to={{ x: xMax, y: 0 }}
                      stroke="#707070"
                      strokeWidth={2}
                    />
                    {axis.ticks.map((tick, i) => {
                      const tickX = tick.to.x + 5;
                      const tickY = tick.to.y + axis.tickLength + 5;
                      return (
                        <Group key={tick.value + i.toString()}>
                          <Line
                            from={tick.from}
                            to={tick.to}
                            stroke="#1b1a1e"
                          />
                          <text
                            transform={`translate(${tickX}, ${tickY}) rotate(-20)`}
                            fontSize={11}
                            textAnchor="end"
                            fill="#707070"
                          >
                            {tick.formattedValue}
                          </text>
                        </Group>
                      );
                    })}
                    <text textAnchor="middle" fontSize="8">
                      {axis.label}
                    </text>
                  </g>
                )}
              </AxisBottom>
            )}

            {showYAxis && (
              <AxisLeft
                scale={yScale}
                top={0}
                left={0}
                tickLabelProps={() => ({
                  fill: '#707070',
                  textAnchor: 'end',
                  fontSize: 11,
                  dx: '-0.25em',
                  dy: '0.25em',
                })}
                stroke="#1b1a1e"
                tickTextFill="#1b1a1e"
                tickFormat={value => yTickFormat(value)}
                numTicks={GraphComponent.numTicksForHeight(graphHeight)}
              />
            )}

            {showXGrid && (
              <GridColumns
                scale={xScale}
                height={yMax}
                stroke="rgba(0,0,0,0.1)"
                strokeWidth={1}
              />
            )}

            {showYGrid && (
              <GridRows
                scale={yScale}
                width={xMax}
                stroke="rgba(0,0,0,0.1)"
                strokeWidth={1}
              />
            )}
          </Group>

          {graphs.map(({ name, render }) =>
            render({ margin, xScale, yScale, xMax, yMax, name })
          )}

          {storePrices.length > 0 && (
            <StorePriceCurve
              margin={margin}
              xScale={xScale}
              yScale={yScale}
              curves={storePriceCurves}
              startDate={startDate}
              endDate={endDate}
            />
          )}

          <Group top={margin.top} left={margin.left}>
            <Bar
              x={0}
              y={0}
              width={width}
              height={graphHeight}
              fill="transparent"
              rx={14}
              data={data}
              onTouchStart={event =>
                showTooltip &&
                this.handleTooltip({
                  event,
                  xFunc,
                  xScale,
                  yScale,
                  data,
                })
              }
              onTouchMove={event =>
                showTooltip &&
                this.handleTooltip({
                  event,
                  xFunc,
                  xScale,
                  yScale,
                  data,
                })
              }
              onMouseMove={event =>
                showTooltip &&
                this.handleTooltip({
                  event,
                  xFunc,
                  xScale,
                  yScale,
                  data,
                })
              }
              onMouseLeave={() => hideTooltip()}
              onTouchEnd={() => hideTooltip()}
            />
          </Group>

          {showGraphTooltip && tooltipData && (
            <Group top={margin.top} left={margin.left}>
              <g style={{ pointerEvents: 'none' }}>
                <Line
                  from={{ x: tooltipLeft, y: 0 }}
                  to={{ x: tooltipLeft, y: yMax }}
                  stroke="rgba(92, 119, 235, 1)"
                  strokeWidth={1}
                  strokeDasharray="2,2"
                />
                <circle
                  cx={tooltipLeft}
                  cy={tooltipTop + 1}
                  r={4}
                  fill="black"
                  fillOpacity={0.1}
                  stroke="black"
                  strokeOpacity={0.1}
                  strokeWidth={2}
                />
                <circle
                  cx={tooltipLeft}
                  cy={tooltipTop}
                  r={4}
                  fill="rgba(92, 119, 235, 1)"
                  stroke="white"
                  strokeWidth={2}
                />
              </g>
            </Group>
          )}
        </svg>

        {showGraphTooltip && tooltipData && (
          <Tooltip
            top={
              // On small touch devices the tooltip will be covered by your finger
              // so we just place it ontop instead.
              tooltipData.isSmallDevice
                ? this.margin.top
                : tooltipTop + this.margin.bottom + this.margin.top
            }
            left={
              // Stops the tooltip from moving outside the Graph bounds
              tooltipData.tooltipBound && tooltipData.tooltipBound < 0
                ? tooltipLeft + this.margin.left + tooltipData.tooltipBound
                : tooltipLeft + this.margin.left
            }
            style={{ padding: 0, zIndex: 99 }}
          >
            <div
              style={S.Tooltip}
              ref={tooltip => {
                this.tooltip = tooltip;
              }}
            >
              {this.props.renderTooltip(tooltipData)}
            </div>
          </Tooltip>
        )}
      </>
    );
  }
}

const {
  bool,
  object,
  arrayOf,
  string,
  shape,
  number,
  func,
  instanceOf,
  oneOfType,
} = PropTypes;

GraphComponent.contextType = AppContext;

GraphComponent.propTypes = {
  nodes: arrayOf(
    arrayOf(
      shape({
        date: oneOfType([instanceOf(Date), string]),
        value: oneOfType([string, number]),
      })
    )
  ).isRequired,
  width: number,
  height: number,
  aspectRatio: number,
  margin: shape({
    top: number,
    bottom: number,
    left: number,
    right: number,
  }),
  showXGrid: bool,
  showYGrid: bool,
  showXAxis: bool,
  showYAxis: bool,
  showGraphTooltip: bool,
  xTickFormat: func,
  yTickFormat: func,
  showLegend: bool,
  hideTooltip: func,
  tooltipData: object,
  tooltipTop: number,
  tooltipLeft: number,
  showTooltip: func,
  renderTooltip: func,
  graphs: arrayOf(
    shape({
      name: string,
      legend: shape({
        label: string,
        color: string,
        lineType: string,
        description: string,
      }),
      render: func,
    })
  ),
  storePrices: PropTypes.arrayOf(
    PropTypes.shape({
      prices: PropTypes.arrayOf(
        PropTypes.shape({
          latestPriceChange: PropTypes.shape({
            time: PropTypes.string,
          }),
          price: PropTypes.number,
        })
      ),
    })
  ),
  storePriceColors: PropTypes.object,
  legend: PropTypes.node,
  xScaleData: PropTypes.array,
  yScaleData: PropTypes.array,
};

GraphComponent.defaultProps = {
  width: null,
  height: null,
  aspectRatio: 2,
  margin: undefined,
  showXGrid: true,
  showYGrid: true,
  showXAxis: true,
  showYAxis: true,
  showGraphTooltip: true,
  xTickFormat: () => {},
  yTickFormat: () => {},
  showLegend: true,
  hideTooltip: () => {},
  tooltipData: undefined,
  tooltipTop: null,
  tooltipLeft: null,
  showTooltip: () => {},
  renderTooltip: ({ xValue, yValue }) => (
    <>
      <div>{xValue}</div>
      <div>{yValue}</div>
    </>
  ),
  graphs: [],
  storePrices: [],
  storePriceColors: {},
  legend: undefined,
  xScaleData: [],
  yScaleData: [],
};

const Graph = props =>
  props.responsive ? (
    <ParentSize>
      {({ width }) => <GraphComponent {...props} width={width} />}
    </ParentSize>
  ) : (
    <GraphComponent {...props} />
  );

Graph.propTypes = {
  responsive: PropTypes.bool,
};

Graph.defaultProps = {
  responsive: false,
};

export default withTooltip(Graph);
